import React, { useEffect, useState } from "react";
import { getApiUrl, BASE_URL } from "../utils/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useFormik } from "formik";
import * as Yup from "yup";

const Bskuser = () => {
  const [orders, setOrders] = useState([]);
  const [user, setUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const ordersPerPage = 5;

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);

  const guestId = localStorage.getItem("userId");

  // ---------- LOAD STATES ----------
  const loadStates = async () => {
    try {
      const res = await fetch(getApiUrl("state-ui-list"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.success) setStates(data.data);
    } catch {
      toast.error("Failed to load states");
    }
  };

  // ---------- LOAD USER ----------
  const loadUser = async () => {
    try {
      const res = await fetch(getApiUrl("user/get"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: guestId }),
      });
      const data = await res.json();
      if (data.success) {
        setUser(data.data);

        // set districts for the state
        const match = data.data.state
          ? states.find((s) => s.state === data.data.state)
          : null;
        setDistricts(match ? match.districts : []);
      }
    } catch {
      toast.error("Failed to load user data");
    }
  };

  // ---------- LOAD ORDERS ----------
  const loadOrders = async (page = 1) => {
    try {
      const res = await fetch(getApiUrl("order-ui-list"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: guestId, page, limit: ordersPerPage }),
      });
      const data = await res.json();
      if (data.success) {
        setOrders(data.data);
        setCurrentPage(data.pagination.page);
        setTotalPages(data.pagination.pages);
      } else {
        setOrders([]);
      }
    } catch {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      await loadStates();
    };
    init();
  }, []);

  useEffect(() => {
    if (states.length) {
      loadUser();
      loadOrders(currentPage);
    }
  }, [states]);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    loadOrders(page);
  };

  // ---------- FORM ----------
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: user?.name || "",
      phone: user?.phone || "",
      email: user?.email || "",
      state: user?.state || "",
      district: user?.district || "",
      address: user?.address || "",
      pincode: user?.pincode || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      phone: Yup.string()
        .matches(/^\d{10}$/, "Enter valid 10-digit phone")
        .required("Phone is required"),
      state: Yup.string().required("State is required"),
      district: Yup.string().required("District is required"),
      address: Yup.string().required("Address is required"),
      pincode: Yup.string()
        .matches(/^[1-9][0-9]{5}$/, "Enter valid 6-digit pincode")
        .required("Pincode is required"),
    }),
    onSubmit: async (values) => {
      try {
        const res = await fetch(getApiUrl("checkout-add"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...values, guestId }),
        });
        const data = await res.json();
        if (data.success) {
          toast.success(editMode ? "Details updated" : "Details saved");
          setUser(values);
          setEditMode(false);
        } else toast.error(data.message || "Failed to save");
      } catch {
        toast.error("Server error");
      }
    },
  });

  const handleStateChange = (e) => {
    const value = e.target.value;
    formik.setFieldValue("state", value);
    formik.setFieldValue("district", "");

    const match = states.find((s) => s.state === value);
    setDistricts(match ? match.districts : []);
  };

  if (loading)
    return (
      <div className="text-center mt-5">
        <div className="spinner-border"></div>
      </div>
    );

  return (
    <div className="container my-4">
      <ToastContainer />

      <h3 className="fw-bold mb-4">My Account</h3>

      {/* ORDERS */}
      <div className="card p-4 mb-4 shadow-sm border-0">
        <h5 className="mb-3 fw-semibold">My Orders</h5>
        {orders.length === 0 ? (
          <p className="text-muted">
            You haven’t placed any orders yet. Make an order with combo.
          </p>
        ) : (
          <>
           {orders.map((o, i) => {
  const isPaid = o.paymentStatus === "SUCCESS";
  const isFailed = o.paymentStatus === "FAILED";
  const shipDone = o.shipStatus === "shipped" || o.shipStatus === "delivered";
  const isDelivered = o.shipStatus === "delivered";

  const steps = [
    { label: "Ordered", icon: "ti-check", done: true },
    { label: "Paid", icon: "ti-credit-card", done: isPaid },
    { label: "Processing", icon: "ti-package", done: shipDone || isDelivered },
    { label: "Shipped", icon: "ti-truck", done: isDelivered, active: o.shipStatus === "shipped" },
    { label: "Delivered", icon: "ti-home", done: isDelivered },
  ];

  const getBanner = () => {
    if (isFailed) return { cls: "danger", icon: "ti-circle-x", title: "Payment failed", sub: "Your order could not be placed. Please try again." };
    if (isPaid && o.shipStatus === "shipped") return { cls: "success", icon: "ti-truck", title: "Order shipped", sub: "On the way · Expected in 3–5 days" };
    if (isPaid && isDelivered) return { cls: "success", icon: "ti-circle-check", title: "Order delivered", sub: "Your order has been delivered" };
    if (isPaid) return { cls: "success", icon: "ti-circle-check", title: "Payment confirmed", sub: "Your order has been placed successfully" };
    return { cls: "warning", icon: "ti-clock", title: "Payment pending", sub: "We're verifying your payment" };
  };

  const banner = getBanner();

  return (
    <div key={o._id || i} className="border rounded-3 p-3 mb-3 shadow-sm">

      {/* BANNER */}
      <div className={`alert alert-${banner.cls === "danger" ? "danger" : banner.cls === "warning" ? "warning" : "success"} d-flex align-items-center gap-2 py-2 px-3 mb-3`}>
        <i className={`ti ${banner.icon} fs-5`}></i>
        <div>
          <div className="fw-500">{banner.title}</div>
          <small>{banner.sub}</small>
        </div>
      </div>

      {/* ORDER META */}
      <div className="d-flex justify-content-between mb-3">
        <div>
          <div className="fw-semibold" style={{ color: "#2563eb" }}>Order #{o.orderUnique}</div>
          <small className="text-muted">{new Date(o.createdAt).toLocaleDateString()} · ₹{o.amount}</small>
        </div>
      </div>

      {/* TRACKER — only show for non-failed */}
      {!isFailed && (
        <div className="d-flex align-items-center mb-3">
          {steps.map((step, idx) => (
            <React.Fragment key={idx}>
              <div className="d-flex flex-column align-items-center" style={{ flex: 1 }}>
                <div className={`rounded-circle d-flex align-items-center justify-content-center border 
                  ${step.done ? "bg-success border-success text-white" : step.active ? "bg-warning border-warning text-dark" : "bg-light border-secondary text-secondary"}`}
                  style={{ width: 30, height: 30, fontSize: 13 }}>
                  <i className={`ti ${step.icon}`}></i>
                </div>
                <small className={`mt-1 text-center ${step.done ? "text-success" : step.active ? "text-warning fw-semibold" : "text-muted"}`}
                  style={{ fontSize: 10 }}>
                  {step.label}
                </small>
              </div>
              {idx < steps.length - 1 && (
                <div style={{ flex: 1, height: 2, background: step.done ? "#198754" : "#dee2e6", marginBottom: 18 }}></div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* PRODUCTS */}
      {o.products.map((p, idx) => (
        <div key={idx} className="d-flex align-items-center py-2 border-bottom gap-3">
          <img
            src={p.productInfo?.mainImage ? `${BASE_URL}${p.productInfo.mainImage}` : `${BASE_URL}/image/default.jpg`}
            width="46" height="46" className="rounded object-fit-cover flex-shrink-0"
            onError={(e) => (e.target.src = `${BASE_URL}/image/default.jpg`)} alt="Product"
          />
          <div className="flex-grow-1">
            <div className="fw-semibold small">{p.productInfo?.productName || p.name}</div>
            <small className="text-muted">Size: {p.size} · Qty: {p.qty}</small>
          </div>
          <div className="fw-semibold">₹{p.price}</div>
        </div>
      ))}

      {/* TOTAL */}
      <div className="d-flex justify-content-between pt-2 mt-1">
        <small className="text-muted">Total paid</small>
        <span className="fw-semibold">₹{o.amount}</span>
      </div>

      {/* INFO NOTE — only when paid but not yet shipped */}
      {isPaid && !shipDone && (
        <div className="alert alert-info d-flex gap-2 py-2 px-3 mt-2 mb-0 small">
          <i className="ti ti-info-circle fs-6 flex-shrink-0 mt-1"></i>
          <span>Your order is being packed. Shipping details will be updated within 2 days.</span>
        </div>
      )}
    </div>
  );
})}

            <div className="d-flex justify-content-center mt-3">
              <button
                className="btn btn-outline-primary me-2"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                Prev
              </button>

              <span className="align-self-center mx-2">
                Page {currentPage} of {totalPages}
              </span>

              <button
                className="btn btn-outline-primary ms-2"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

      {/* USER DETAILS */}
      {user && !editMode && (
        <div className="card p-4 shadow-sm border-0">
          <h5 className="mb-3 fw-semibold">User Details</h5>
          <p>
            <b>Name:</b> {user.name}
          </p>
          <p>
            <b>Phone:</b> {user.phone}
          </p>
          {/* <p>
            <b>Email:</b> {user.email || "-"}
          </p> */}
          <p>
            <b>State:</b> {user.state}
          </p>
          <p>
            <b>District:</b> {user.district}
          </p>
          <p>
            <b>Address:</b> {user.address}
          </p>
          <p>
            <b>Pincode:</b> {user.pincode}
          </p>
          <button
            className="btn btn-warning mt-2"
            onClick={() => setEditMode(true)}
          >
            Edit
          </button>
        </div>
      )}

      {/* EDIT FORM */}
      {user && editMode && (
        <div className="card p-4 shadow-sm border-0">
          <h5 className="mb-3 fw-semibold">Edit Details</h5>
          <form onSubmit={formik.handleSubmit}>
            <div className="d-flex flex-wrap">
              <div className="col-md-6 col-12 mb-3">
                <input
                  type="text"
                  name="name"
                  className="form-control"
                  placeholder="Name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.name && formik.errors.name && (
                  <small className="text-danger">{formik.errors.name}</small>
                )}
              </div>

              <div className="col-md-6 col-12 mb-3">
                <input
                  type="tel"
                  name="phone"
                  className="form-control"
                  placeholder="Phone"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.phone && formik.errors.phone && (
                  <small className="text-danger">{formik.errors.phone}</small>
                )}
              </div>

              <div className="col-md-6 col-12 mb-3">
                <select
                  name="state"
                  className="form-select"
                  value={formik.values.state}
                  onChange={handleStateChange}
                >
                  <option value="">Select State</option>
                  {states.map((s) => (
                    <option key={s._id} value={s.state}>
                      {s.state}
                    </option>
                  ))}
                </select>
                {formik.touched.state && formik.errors.state && (
                  <small className="text-danger">{formik.errors.state}</small>
                )}
              </div>

              <div className="col-md-6 col-12 mb-3">
                <select
                  name="district"
                  className="form-select"
                  value={formik.values.district}
                  onChange={formik.handleChange}
                  disabled={!districts.length}
                >
                  <option value="">Select District</option>
                  {districts.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
                {formik.touched.district && formik.errors.district && (
                  <small className="text-danger">
                    {formik.errors.district}
                  </small>
                )}
              </div>

              <div className="col-12 mb-3">
                <textarea
                  name="address"
                  className="form-control"
                  rows={3}
                  placeholder="Address"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.address && formik.errors.address && (
                  <small className="text-danger">{formik.errors.address}</small>
                )}
              </div>

              <div className="col-12 mb-3">
                <input
                  type="text"
                  name="pincode"
                  className="form-control"
                  placeholder="Pincode"
                  value={formik.values.pincode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.pincode && formik.errors.pincode && (
                  <small className="text-danger">{formik.errors.pincode}</small>
                )}
              </div>

              {/* <div className="col-md-6 col-12 mb-3">
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  placeholder="Email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div> */}

              <div className="col-12 text-center">
                <button type="submit" className="btn btn-primary me-2">
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setEditMode(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Bskuser;
